<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <div class="mb-2">
          <h6 class="fw-medium d-flex align-items-center">
            <router-link to="/tickets/ticket">
              <i class="ti ti-arrow-left me-2"></i>Ticket Details
            </router-link>
          </h6>
        </div>

        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <div class="me-2 mb-2">
            <button
              type="button"
              class="btn btn-white d-inline-flex align-items-center"
              @click="reload"
              :disabled="store.loading"
            >
              <i class="ti ti-refresh me-1"></i>Refresh
            </button>
          </div>

          <div class="mb-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="modal"
              data-bs-target="#add_ticket"
              class="btn btn-primary d-flex align-items-center"
              v-can="'Ticket.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Ticket
            </a>
          </div>

          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="row" v-if="ticket">
        <div class="col-xl-9 col-md-8">
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
            >
              <h5 class="text-info fw-medium">
                {{ ticket.category?.name || "IT Support" }}
              </h5>

              <div class="d-flex align-items-center">
                <span
                  class="badge me-3"
                  :class="priorityBadge(ticket.priority)"
                >
                  <i class="ti ti-circle-filled fs-5 me-1"></i
                  >{{ ticket.priority }}
                </span>

                <!-- ✅ Visibility (real update) -->
                <div v-can="'Ticket.Edit'">
                  <vue-select
                    :options="['Public', 'Private']"
                    :modelValue="ticket.visibility"
                    @update:modelValue="(v) => updateTicket({ visibility: v })"
                    placeholder="Visibility"
                  />
                </div>
              </div>
            </div>

            <div class="card-body">
              <div>
                <div
                  class="d-flex align-items-center justify-content-between flex-wrap border-bottom mb-3"
                >
                  <div class="d-flex align-items-center flex-wrap">
                    <div class="mb-3">
                      <span class="badge badge-info rounded-pill mb-2">{{
                        ticket.code
                      }}</span>

                      <div class="d-flex align-items-center mb-2">
                        <h5 class="fw-semibold me-2">{{ ticket.subject }}</h5>
                        <span
                          class="badge d-flex align-items-center ms-1"
                          :class="statusOutlineClass(ticket.status)"
                        >
                          <i class="ti ti-circle-filled fs-5 me-1"></i
                          >{{ ticket.status }}
                        </span>
                      </div>

                      <div
                        class="d-flex align-items-center flex-wrap row-gap-2"
                      >
                        <p class="d-flex align-items-center mb-0 me-2">
                          <img
                            :src="avatar"
                            class="avatar avatar-xs rounded-circle me-2"
                            alt="img"
                          />
                          Assigned to
                          <span class="text-dark ms-1">
                            {{
                              fullName(ticket.assignee?.user) || "Unassigned"
                            }}
                          </span>
                        </p>

                        <p class="d-flex align-items-center mb-0 me-2">
                          <i class="ti ti-calendar-bolt me-1"></i>Updated
                          {{ new Date(ticket.updated_at).toLocaleString() }}
                        </p>

                        <p class="d-flex align-items-center mb-0">
                          <i class="ti ti-message-circle-share me-1"></i>
                          {{ visibleComments.length }} Comments
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="mb-3 d-flex flex-wrap gap-2">
                    <a
                      href="javascript:void(0);"
                      class="btn btn-primary"
                      data-bs-toggle="modal"
                      data-bs-target="#reply_ticket_modal"
                      v-can="'Ticket.Read'"
                    >
                      <i class="ti ti-arrow-forward-up me-1"></i>Post a Reply
                    </a>

                    <button
                      v-if="ticket.status !== 'Closed'"
                      class="btn btn-success"
                      @click="updateTicket({ status: 'Closed' })"
                      v-can="'Ticket.Edit'"
                    >
                      <i class="ti ti-checks me-1"></i>Close
                    </button>

                    <button
                      v-else
                      class="btn btn-warning"
                      @click="updateTicket({ status: 'Reopened' })"
                      v-can="'Ticket.Edit'"
                    >
                      <i class="ti ti-refresh me-1"></i>Reopen
                    </button>

                    <button
                      class="btn btn-outline-dark"
                      @click="
                        updateTicket({
                          visibility:
                            ticket.visibility === 'Public'
                              ? 'Private'
                              : 'Public',
                        })
                      "
                      v-can="'Ticket.Edit'"
                    >
                      <i class="ti ti-eye-off me-1"></i>
                      {{
                        ticket.visibility === "Public"
                          ? "Make Private"
                          : "Make Public"
                      }}
                    </button>
                  </div>
                </div>

                <!-- Ticket Description -->
                <div class="mb-4">
                  <p class="mb-3" v-if="ticket.description">
                    {{ ticket.description }}
                  </p>
                </div>

                <!-- ✅ Comments (filtered: hide internal unless staff) -->
                <div
                  v-for="c in visibleComments"
                  :key="c.id"
                  class="border-bottom mb-3 pb-3"
                >
                  <div class="d-flex align-items-center mb-3">
                    <span
                      class="avatar avatar-lg avatar-rounded me-2 flex-shrink-0"
                    >
                      <img :src="avatar" alt="Img" />
                    </span>
                    <div>
                      <h6 class="fw-medium mb-1">
                        {{ fullName(c.user) }}
                        <span
                          v-if="c.is_internal"
                          class="badge bg-dark ms-2"
                          v-can="'Ticket.Edit'"
                        >
                          Internal
                        </span>
                      </h6>
                      <p class="mb-0">
                        <i class="ti ti-calendar-bolt me-1"></i
                        >{{ new Date(c.created_at).toLocaleString() }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-3">
                    <p class="mb-0">{{ c.body }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right sidebar -->
        <div class="col-xl-3 col-md-4">
          <div class="card">
            <div class="card-header p-3"><h4>Ticket Details</h4></div>
            <div class="card-body p-0">
              <div class="border-bottom p-3">
                <div class="mb-3" v-can="'Ticket.Edit'">
                  <label class="form-label">Change Priority</label>
                  <vue-select
                    :options="['Low', 'Medium', 'High', 'Urgent']"
                    :modelValue="ticket.priority"
                    @update:modelValue="(v) => updateTicket({ priority: v })"
                  />
                </div>

                <div class="mb-3" v-can="'Ticket.Edit'">
                  <label class="form-label">Assign To</label>
                  <vue-select
                    :options="assigneeOptions"
                    label="text"
                    :reduce="(o) => o.id"
                    :modelValue="ticket.assignee_id"
                    @update:modelValue="(v) => updateTicket({ assignee_id: v })"
                    placeholder="Select agent"
                  />
                </div>

                <div v-can="'Ticket.Edit'">
                  <label class="form-label">Ticket Status</label>
                  <vue-select
                    :options="['Open', 'On Hold', 'Reopened', 'Closed']"
                    :modelValue="ticket.status"
                    @update:modelValue="(v) => updateTicket({ status: v })"
                  />
                </div>
              </div>

              <div class="d-flex align-items-center border-bottom p-3">
                <span class="avatar avatar-md me-2 flex-shrink-0">
                  <img :src="avatar" class="rounded-circle" alt="Img" />
                </span>
                <div>
                  <span class="fs-12">User</span>
                  <p class="text-dark mb-0">
                    {{ fullName(ticket.requester?.user) }}
                  </p>
                </div>
              </div>

              <div
                class="d-flex align-items-center border-bottom p-3"
                v-if="ticket.assignee"
              >
                <span class="avatar avatar-md me-2 flex-shrink-0">
                  <img :src="avatar" class="rounded-circle" alt="Img" />
                </span>
                <div>
                  <span class="fs-12">Support Agent</span>
                  <p class="text-dark mb-0">
                    {{ fullName(ticket.assignee?.user) }}
                  </p>
                </div>
              </div>

              <div class="border-bottom p-3" v-if="ticket.category">
                <span class="fs-12">Category</span>
                <p class="text-dark mb-0">{{ ticket.category?.name }}</p>
              </div>

              <div class="border-bottom p-3">
                <span class="fs-12">Email</span>
                <p class="text-dark mb-0">{{ requesterEmail }}</p>
              </div>

              <div class="p-3">
                <span class="fs-12">Last Updated / Closed On</span>
                <p class="text-dark mb-0">
                  {{
                    ticket.closed_at
                      ? new Date(ticket.closed_at).toLocaleDateString()
                      : new Date(ticket.updated_at).toLocaleDateString()
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-5">Loading...</div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <!-- Modals -->
  <tickets-modal
    :edit-item="editItem"
    @saved="reload"
    @cleared-edit="editItem = null"
  ></tickets-modal>

  <ticket-details-modal
    v-if="ticket"
    :ticket-id="ticket.id"
    :ticket="ticket"
    @saved="reload"
  />
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useTicketStore } from "@/stores/ticketStore";
import TicketsModal from "@/components/modal/tickets-modal.vue";
import TicketDetailsModal from "@/components/modal/ticket-details-modal.vue";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useAuthStore } from "@/stores/authStore";

const store = useTicketStore();
const empStore = useEmployeeStore();
const auth = useAuthStore();

const route = useRoute();
const editItem = ref(null);

const avatar = require("@/assets/img/profiles/avatar-01.jpg");

const ticket = computed(() => store.item);
const requesterEmail = computed(
  () => ticket.value?.requester?.user?.email || "-"
);

/* ✅ staff check for internal note visibility */
const isStaff = computed(
  () =>
    auth.can("Ticket.Edit") ||
    auth.can("Ticket.Delete") ||
    auth.can("Ticket.Write")
);

const visibleComments = computed(() => {
  const all = ticket.value?.comments || [];
  return isStaff.value ? all : all.filter((c) => !c.is_internal);
});

const assigneeOptions = computed(() =>
  (empStore.employees || []).map((e) => {
    const u = e.user || {};
    return {
      id: e.id,
      text: [u.first_name || e.first_name, u.last_name || e.last_name]
        .filter(Boolean)
        .join(" "),
    };
  })
);

function fullName(u) {
  if (!u) return "";
  return [u.first_name, u.last_name].filter(Boolean).join(" ");
}

function priorityBadge(p) {
  return {
    "badge bg-danger": p === "High" || p === "Urgent",
    "badge bg-warning": p === "Medium",
    "badge bg-success": p === "Low",
  };
}
function statusOutlineClass(s) {
  return {
    "badge bg-outline-pink d-flex align-items-center": s === "Open",
    "badge bg-outline-warning d-flex align-items-center": s === "On Hold",
    "badge bg-outline-purple d-flex align-items-center": s === "Reopened",
    "badge bg-outline-success d-flex align-items-center": s === "Closed",
  };
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

/* ✅ mark ticket as seen (localStorage) */
function markSeen(id) {
  if (!id) return;
  localStorage.setItem(`ticket:last_seen:${id}`, String(Date.now()));
}

async function updateTicket(payload) {
  if (!ticket.value?.id) return;
  await store.update(ticket.value.id, payload);
  await store.fetchOne(ticket.value.id, { force: true });
}

function reload() {
  const id = route.query.id;
  if (id) store.fetchOne(id, { force: true });
}

onMounted(async () => {
  const id = route.query.id;
  if (id) {
    await store.ensureOne(id);
    markSeen(id);
  }
  if (!empStore.employees?.length && empStore.ensureEmployees) {
    await empStore.ensureEmployees();
  }
});
</script>


